import { useSyncExternalStore } from "react";
import { Fehler } from "./klient";
import { FACH_MAPPE, lesen, schreiben } from "./speicher";

/* ══════════════════════════════════════════════════════════════════
   Die Mappe — was ohne Netz noch dasteht.

   09-mobile.md: *„Die Tagesliste wird beim Öffnen gecacht."* Der Satz
   dahinter ist der wichtigere: **Was ohne Netz sichtbar bleibt,
   entscheidet die Anwendung — nicht der Service Worker.** Der cacht den
   Rumpf der Anwendung und fasst `/api` nie an (public/sw.js). Eine
   zwischengespeicherte API-Antwort, die als frisch durchgeht, ist
   schlimmer als gar keine: Der Fahrer sähe die Tagesliste von gestern
   und wüsste es nicht.

   Deshalb liegt hier nur, was der Fahrer **mitnimmt**, und es steht
   immer mit seinem Stand dabei:

   | `handy/heute`  | RAUS, REIN, VORBEREITEN |
   | `handy/einsatz`| der eigene Einsatz und die Stempeluhr |

   **Was ausdrücklich nicht hierher gehört:** Verfügbarkeit, Preise,
   Angebote, Kundenakten. Sie ändern sich, während der Fahrer unterwegs
   ist, und eine alte Auskunft darüber ist eine falsche Auskunft. Der
   Schirm sagt das (`offline.nachschlagen`), statt eine leere Liste zu
   zeigen.
   ══════════════════════════════════════════════════════════════════ */

type Blatt = { weg: string; stand: string; daten: unknown };

/** Woher das kam, was gerade auf dem Schirm steht. */
type Lage = { ausMappe: boolean; stand: string | null };

const lagen = new Map<string, Lage>();
const hoerer = new Set<() => void>();
const OHNE: Lage = { ausMappe: false, stand: null };

function melden(): void {
  hoerer.forEach((h) => h());
}

function abonnieren(hoer: () => void): () => void {
  hoerer.add(hoer);
  return () => hoerer.delete(hoer);
}

/**
 * Ob das, was unter `weg` zu sehen ist, aus der Mappe kommt — und von wann.
 *
 * Die Maske zeigt daraus einen Satz. Sie fragt **nicht** `navigator.onLine`:
 * Ein Telefon im WLAN eines Baucontainers ohne Gegenstelle ist „online" und
 * bekommt trotzdem nichts.
 */
export function useMappenlage(weg: string): Lage {
  return useSyncExternalStore(
    abonnieren,
    () => lagen.get(weg) ?? OHNE,
    () => OHNE,
  );
}

function setzen(weg: string, lage: Lage): void {
  const alt = lagen.get(weg);
  if (alt && alt.ausMappe === lage.ausMappe && alt.stand === lage.stand) return;
  lagen.set(weg, lage);
  melden();
}

/**
 * Holt frisch — und legt das Ergebnis in die Mappe. Geht das Netz nicht, kommt
 * der letzte Stand zurück.
 *
 * **Nur beim Netzfehler.** Ein `403` oder ein `422` ist eine Antwort, und sie
 * gehört auf den Schirm; sie durch einen alten Stand zu ersetzen hieße, einen
 * Fehler zu verstecken. Liegt auch nichts in der Mappe, geht der Fehler
 * durch — dann steht dort „ohne Netz" und keine leere Liste.
 */
export async function mitMappe<T>(weg: string, holen: () => Promise<T>): Promise<T> {
  try {
    const frisch = await holen();
    const stand = new Date().toISOString();
    await schreiben(FACH_MAPPE, { weg, stand, daten: frisch } satisfies Blatt);
    setzen(weg, { ausMappe: false, stand });
    return frisch;
  } catch (fehler) {
    const amNetz = fehler instanceof Fehler
      ? fehler.schluessel === "netz" || fehler.status === 0
      : true;
    if (!amNetz) throw fehler;
    const blatt = await lesen<Blatt>(FACH_MAPPE, weg);
    if (!blatt) {
      setzen(weg, { ausMappe: false, stand: null });
      throw fehler;
    }
    setzen(weg, { ausMappe: true, stand: blatt.stand });
    return blatt.daten as T;
  }
}
